// Spaces: the groups the Spaces tab shows. Three kinds, all built from the
// same cards with no network:
//   board   - the user's own boards (what the canvas calls folders)
//   source  - automatic, by where the item came from (Instagram Reels, X Bookmarks, ...)
//   subject - automatic, by AI subject label (Interior, Cars, ...); empty until tagging runs
//
// Boards nest (Board.parent_id). A board space holds its own cards and every
// descendant board's cards, so a parent never looks emptier than its
// children. "Your spaces" lists the top-level boards; an open board space
// lists its direct children with buildChildBoardSpaces.

import type { Board } from '@/stores/canvasStore';
import { byNewest, type LibraryCard } from './libraryCard';
import { LIBRARY_SOURCES, type LibrarySource } from './sources';

export type LibrarySpaceKind = 'board' | 'source' | 'subject';

export interface LibrarySpace {
  /** Stable across renders: `board:<id>`, `source:<key>`, `subject:<label>`. */
  id: string;
  kind: LibrarySpaceKind;
  label: string;
  /** Newest first. For boards, includes every descendant board's cards. */
  cards: LibraryCard[];
  /** Up to three cards for the cover stack, newest with a picture first. */
  covers: LibraryCard[];
  boardId?: string;
  /** Board spaces: ids of direct child boards that hold anything. */
  childBoardIds?: string[];
  source?: LibrarySource;
  subject?: string;
}

/** Spaces with fewer cards than this are not worth a subject space. */
export const SUBJECT_MIN_COUNT = 3;

function newestFirst(cards: LibraryCard[]): LibraryCard[] {
  return [...cards].sort(byNewest);
}

/**
 * The cover pile: up to three cards, newest pictures first, then text cards
 * when there are not enough pictures. Two cards with the same picture (a
 * repeated save) count once so the pile never shows a duplicate.
 */
export function pickCovers(cards: LibraryCard[]): LibraryCard[] {
  const sorted = newestFirst(cards);
  const seen = new Set<string>();
  const withImage: LibraryCard[] = [];
  for (const c of sorted) {
    if (!c.thumbnail || seen.has(c.thumbnail)) continue;
    seen.add(c.thumbnail);
    withImage.push(c);
    if (withImage.length === 3) return withImage;
  }
  const rest = sorted.filter((c) => !c.thumbnail);
  return [...withImage, ...rest].slice(0, 3);
}

function space(id: string, kind: LibrarySpaceKind, label: string, cards: LibraryCard[], extra: Partial<LibrarySpace> = {}): LibrarySpace {
  const sorted = newestFirst(cards);
  return { id, kind, label, cards: sorted, covers: pickCovers(sorted), ...extra };
}

/** Biggest first; ties go to the space with the newest card, then by name. */
function bySizeThenRecency(a: LibrarySpace, b: LibrarySpace): number {
  if (b.cards.length !== a.cards.length) return b.cards.length - a.cards.length;
  const recency = (b.cards[0]?.feedTime ?? 0) - (a.cards[0]?.feedTime ?? 0);
  return recency || a.label.localeCompare(b.label);
}

interface BoardTree {
  byId: Map<string, Board>;
  children: Map<string, Board[]>;
  /** Cards directly on each board. */
  own: Map<string, LibraryCard[]>;
}

function boardTree(cards: LibraryCard[], boards: Board[]): BoardTree {
  const byId = new Map(boards.map((b) => [b.id, b]));
  const children = new Map<string, Board[]>();
  for (const b of boards) {
    if (!b.parent_id || !byId.has(b.parent_id)) continue;
    const list = children.get(b.parent_id) ?? [];
    list.push(b);
    children.set(b.parent_id, list);
  }
  const own = new Map<string, LibraryCard[]>();
  for (const card of cards) {
    if (!card.boardId) continue;
    const list = own.get(card.boardId) ?? [];
    list.push(card);
    own.set(card.boardId, list);
  }
  return { byId, children, own };
}

/** A board's own cards plus every descendant's. Guards against parent cycles. */
function subtreeCards(tree: BoardTree, boardId: string, seen = new Set<string>()): LibraryCard[] {
  if (seen.has(boardId)) return [];
  seen.add(boardId);
  const out = [...(tree.own.get(boardId) ?? [])];
  for (const child of tree.children.get(boardId) ?? []) out.push(...subtreeCards(tree, child.id, seen));
  return out;
}

function boardSpace(tree: BoardTree, b: Board): LibrarySpace | null {
  const cards = subtreeCards(tree, b.id);
  if (cards.length === 0) return null;
  const childBoardIds = (tree.children.get(b.id) ?? []).filter((c) => subtreeCards(tree, c.id).length > 0).map((c) => c.id);
  return space(`board:${b.id}`, 'board', b.name, cards, { boardId: b.id, childBoardIds });
}

function isRoot(b: Board): boolean {
  return b.parent_id === null;
}

/**
 * Top-level boards with at least one card in them or below them, biggest
 * first. Root boards (Home) are left out: their items are "Everything". A
 * top-level board is one whose parent is a root, or whose parent is missing.
 */
export function buildBoardSpaces(cards: LibraryCard[], boards: Board[]): LibrarySpace[] {
  const tree = boardTree(cards, boards);
  return boards
    .filter((b) => {
      if (isRoot(b)) return false;
      const parent = b.parent_id ? tree.byId.get(b.parent_id) : undefined;
      return !parent || isRoot(parent);
    })
    .map((b) => boardSpace(tree, b))
    .filter((s): s is LibrarySpace => s !== null)
    .sort(bySizeThenRecency);
}

/** The direct child boards of `boardId` that hold anything, biggest first. */
export function buildChildBoardSpaces(cards: LibraryCard[], boards: Board[], boardId: string): LibrarySpace[] {
  const tree = boardTree(cards, boards);
  return (tree.children.get(boardId) ?? [])
    .map((b) => boardSpace(tree, b))
    .filter((s): s is LibrarySpace => s !== null)
    .sort(bySizeThenRecency);
}

/** Any board space by board id, nested or not. Null for roots and empty boards. */
export function findBoardSpace(cards: LibraryCard[], boards: Board[], boardId: string): LibrarySpace | null {
  const tree = boardTree(cards, boards);
  const b = tree.byId.get(boardId);
  if (!b || isRoot(b)) return null;
  return boardSpace(tree, b);
}

/** One space per source that has anything in it, in LIBRARY_SOURCES order. */
export function buildSourceSpaces(cards: LibraryCard[]): LibrarySpace[] {
  const bySource = new Map<LibrarySource, LibraryCard[]>();
  for (const card of cards) {
    const list = bySource.get(card.source) ?? [];
    list.push(card);
    bySource.set(card.source, list);
  }
  return LIBRARY_SOURCES.filter((m) => bySource.has(m.key)).map((m) =>
    space(`source:${m.key}`, 'source', m.label, bySource.get(m.key)!, { source: m.key }),
  );
}

/**
 * One space per subject label used by at least `minCount` cards, biggest
 * first. Labels match without regard to case or surrounding space; the
 * space takes the spelling most cards use.
 */
export function buildSubjectSpaces(cards: LibraryCard[], minCount = SUBJECT_MIN_COUNT): LibrarySpace[] {
  const groups = new Map<string, { cards: LibraryCard[]; spellings: Map<string, number> }>();
  for (const card of cards) {
    const counted = new Set<string>();
    for (const raw of card.subjects) {
      const label = raw.trim();
      const key = label.toLowerCase();
      if (!key || counted.has(key)) continue;
      counted.add(key);
      const group = groups.get(key) ?? { cards: [], spellings: new Map() };
      group.cards.push(card);
      group.spellings.set(label, (group.spellings.get(label) ?? 0) + 1);
      groups.set(key, group);
    }
  }
  return [...groups.values()]
    .filter((g) => g.cards.length >= minCount)
    .map((g) => {
      const label = [...g.spellings.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0][0];
      return space(`subject:${label}`, 'subject', label, g.cards, { subject: label });
    })
    .sort(bySizeThenRecency);
}
